/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, EyeSlash, ArrowSquareOut, Globe, Article, ChartLine,
  House, GameController, Scales, Envelope, PaperPlane, Star,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("rescigo_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  return d || e?.message || "Error";
};

const GROUP_META = {
  Main:       { Icon: House,          color: "#0B2B66" },
  Solutions:  { Icon: ChartLine,      color: "#0A2540" },
  Resources:  { Icon: Article,        color: "#2D5A27" },
  Engagement: { Icon: GameController, color: "#C9A227" },
  Legal:      { Icon: Scales,         color: "#5C5A55" },
  Company:    { Icon: Globe,          color: "#B12A1E" },
};

export default function SitePages() {
  const [pages, setPages] = useState([]);
  const [busy, setBusy] = useState({});
  const [lastCheck, setLastCheck] = useState(null);
  const [sending, setSending] = useState(false);
  const [homePath, setHomePath] = useState("/");
  const [settingHome, setSettingHome] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/site-pages");
      setPages(r.data.pages || []);
      setLastCheck(r.data.last_health_check_at || null);
      setHomePath(r.data.home_path || "/");
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (path, field, value) => {
    const busyKey = `${path}:${field}`;
    setBusy({ ...busy, [busyKey]: true });
    setPages(p => p.map(x => x.path === path
      ? { ...x, ...(field === "page" ? { enabled: value } : { menu_enabled: value }) }
      : x));
    try {
      const payload = { path };
      if (field === "page") payload.enabled = value;
      else payload.menu_enabled = value;
      await api.put("/admin/site-pages/toggle", payload);
      if (field === "page") {
        toast.success(value
          ? "Page is live · admins notified"
          : "Page hidden · admin alert email queued");
      } else {
        toast.success(value
          ? "Menu link visible on public nav"
          : "Menu link hidden from public nav");
      }
    } catch (e) {
      toast.error(fmtErr(e));
      setPages(p => p.map(x => x.path === path
        ? { ...x, ...(field === "page" ? { enabled: !value } : { menu_enabled: !value }) }
        : x));
    }
    setBusy({ ...busy, [busyKey]: false });
  };

  const sendHealthCheck = async () => {
    setSending(true);
    try {
      await api.post("/admin/site-pages/health-check/run");
      toast.success("Health check email sent to all admins");
      setTimeout(load, 800);
    } catch (e) { toast.error(fmtErr(e)); }
    setSending(false);
  };

  const setAsHome = async (path) => {
    if (path === homePath) return;
    setSettingHome(path);
    const prev = homePath;
    setHomePath(path);  // optimistic
    try {
      await api.put("/admin/site-pages/set-home", { path });
      toast.success(path === "/"
        ? "Home reset to default landing page"
        : `Home page set to ${path}`);
    } catch (e) {
      toast.error(fmtErr(e));
      setHomePath(prev);
    }
    setSettingHome(null);
  };

  const groups = pages.reduce((acc, p) => {
    (acc[p.group] ||= []).push(p);
    return acc;
  }, {});

  const total = pages.length;
  const live = pages.filter(p => p.enabled).length;

  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="site-pages-admin">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Site Settings</div>
            <h1 className="font-heading text-3xl mt-1 text-slate-900">Public Pages Visibility</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-xl leading-relaxed">
              Toggle individual marketing pages on/off. Hidden pages disappear from the
              site navigation and show a "coming soon" placeholder if visited directly.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-2.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500"/>
            <div className="text-sm text-slate-700"><b>{live}</b> live <span className="text-slate-400 mx-1">/</span> {total} pages</div>
          </div>
        </header>

        {/* Health-check banner */}
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-50/40 border border-emerald-200/60 rounded-xl p-5 flex items-start gap-4 flex-wrap"
              data-testid="health-check-banner">
          <div className="w-10 h-10 rounded-lg bg-emerald-600/10 grid place-items-center text-emerald-700 flex-none">
            <Envelope size={18} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-800">Daily Site Health Check</div>
            <div className="font-heading text-base text-slate-900 mt-0.5">
              {live === total
                ? "Site is fully live — all pages visible to visitors."
                : `${total - live} page${total - live > 1 ? "s" : ""} currently hidden — admins notified.`}
            </div>
            <div className="text-[12px] text-slate-600 mt-1.5 leading-relaxed">
              Admin alert emails fire <strong>immediately</strong> on every page toggle, plus a
              consolidated summary at <strong>08:00 UTC daily</strong>.
              {lastCheck
                ? <> Last summary sent: <span className="font-mono text-[11px] text-slate-700">{new Date(lastCheck).toLocaleString()}</span>.</>
                : <> No daily summary sent yet.</>}
            </div>
          </div>
          <button onClick={sendHealthCheck} disabled={sending}
                  data-testid="send-health-check"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-60">
            <PaperPlane size={13} weight="bold"/>
            {sending ? "Sending…" : "Send health check now"}
          </button>
        </div>

        {Object.entries(groups).map(([group, rows]) => {
          const meta = GROUP_META[group] || GROUP_META.Company;
          const isMain = group === "Main";
          return (
            <section key={group} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                <meta.Icon size={14} weight="duotone" style={{ color: meta.color }}/>
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-700">{group}</div>
                {isMain && (
                  <div className="ml-2 text-[10px] font-mono text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5"
                       data-testid="current-home-path">
                    Home: <b className="text-slate-800">{homePath}</b>
                  </div>
                )}
                <div className="ml-auto hidden md:flex items-center gap-6 pr-1 text-[9px] uppercase tracking-[0.22em] font-semibold text-slate-400">
                  {isMain && <span className="w-[64px] text-center">Home</span>}
                  <span className="w-[72px] text-center">Page live</span>
                  <span className="w-[72px] text-center">In menu</span>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {rows.map(p => {
                  const menuOn = p.menu_enabled !== false;  // default ON
                  const isHome = isMain && p.path === homePath;
                  return (
                  <div key={p.path} data-testid={`site-page-row-${p.path.slice(1) || "root"}`}
                       className="px-5 py-4 flex items-center gap-4 hover:bg-slate-50/30">
                    <div className={`w-9 h-9 rounded-lg grid place-items-center flex-none ${
                      p.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}>
                      {p.enabled ? <Eye size={14} weight="duotone"/> : <EyeSlash size={14} weight="duotone"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        {p.label}
                        {isHome && (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded"
                                data-testid={`home-badge-${p.path.slice(1) || "root"}`}>
                            <Star size={10} weight="fill"/> Home
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-slate-500 mt-0.5">{p.path}</div>
                      {p.enabled && !menuOn && (
                        <div className="text-[10px] text-amber-700 mt-1 inline-flex items-center gap-1"
                             data-testid={`hidden-menu-badge-${p.path.slice(1) || "root"}`}>
                          Page is live but hidden from nav menu
                        </div>
                      )}
                      {!p.enabled && menuOn && (
                        <div className="text-[10px] text-rose-700 mt-1 inline-flex items-center gap-1"
                             data-testid={`dead-menu-badge-${p.path.slice(1) || "root"}`}>
                          Menu link visible — page itself is hidden
                        </div>
                      )}
                    </div>
                    {p.enabled && (
                      <a href={p.path} target="_blank" rel="noreferrer"
                         className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                         data-testid={`open-${p.path.slice(1) || "root"}`}>
                        <ArrowSquareOut size={11}/> View
                      </a>
                    )}
                    <div className="flex items-center gap-6 pr-1">
                      {isMain && (
                        <div className="flex flex-col items-center gap-1 w-[64px]">
                          <button
                            onClick={() => setAsHome(p.path)}
                            disabled={!p.enabled || settingHome === p.path}
                            data-testid={`set-home-${p.path.slice(1) || "root"}`}
                            title={!p.enabled
                              ? "Enable this page first"
                              : isHome ? "Currently the home page" : "Set as home page"}
                            className={`w-9 h-9 rounded-full grid place-items-center transition-colors ${
                              isHome
                                ? "bg-amber-100 text-amber-700 ring-2 ring-amber-400"
                                : "bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
                            } disabled:opacity-40 disabled:cursor-not-allowed`}>
                            <Star size={16} weight={isHome ? "fill" : "regular"}/>
                          </button>
                          <span className="md:hidden text-[9px] uppercase tracking-[0.2em] text-slate-500">Home</span>
                        </div>
                      )}
                      <div className="flex flex-col items-center gap-1 w-[72px]">
                        <Toggle checked={p.enabled} disabled={!!busy[`${p.path}:page`]}
                                onChange={(v) => toggle(p.path, "page", v)}
                                testid={`toggle-${p.path.slice(1) || "root"}`}/>
                        <span className="md:hidden text-[9px] uppercase tracking-[0.2em] text-slate-500">Page</span>
                      </div>
                      <div className="flex flex-col items-center gap-1 w-[72px]">
                        <Toggle checked={menuOn} disabled={!!busy[`${p.path}:menu`]}
                                onChange={(v) => toggle(p.path, "menu", v)}
                                testid={`toggle-menu-${p.path.slice(1) || "root"}`}/>
                        <span className="md:hidden text-[9px] uppercase tracking-[0.2em] text-slate-500">Menu</span>
                      </div>
                    </div>
                  </div>
                );})}
              </div>
            </section>
          );
        })}

        <div className="text-[11px] text-slate-500 italic leading-relaxed">
          <b>Home</b> star (Main pages only) chooses which page loads at <code>/</code>. Only enabled pages can be picked.&nbsp;
          <b>Page live</b> controls whether the public page itself loads — hidden pages show a "coming soon" screen.&nbsp;
          <b>In menu</b> controls whether the page appears in the public nav dropdown — you can keep a live page off the menu, or keep a menu link pointing to a hidden page.
        </div>
      </div>
    </AppShell>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <label className={`inline-flex items-center cursor-pointer ${disabled ? "opacity-60" : ""}`}>
      <input type="checkbox" checked={!!checked}
             onChange={(e) => onChange(e.target.checked)}
             disabled={disabled} data-testid={testid}
             className="sr-only peer"/>
      <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-emerald-600 transition relative
                      after:content-[''] after:absolute after:top-0.5 after:left-0.5
                      after:w-5 after:h-5 after:bg-white after:rounded-full after:transition
                      peer-checked:after:translate-x-5"/>
    </label>
  );
}
